/**
 * The butter lamp in the lotus — drawn, not illustrated.
 *
 * Follows the same rule as `Icon`: inline SVG, no icon package, no animation
 * library. The line work inherits `currentColor` so it takes the colour of
 * whatever it sits in; only the flame and its glow are gold, because the flame
 * is the one thing on the page that is meant to be warm.
 *
 * Everything that moves does so through `transform` and `opacity` on elements
 * that are already their own layers — see `.lamp-mark__*` in components.css.
 * There is no JavaScript in the loop.
 *
 * Decorative: the heading beside it carries the meaning, so this is hidden from
 * assistive technology rather than described to it.
 */
export default function LampMark({ size = 132, className }) {
  return (
    <svg
      className={['lamp-mark', className].filter(Boolean).join(' ')}
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* The halo. Gold at the centre, gone by the edge — a light source, not
            a ring drawn around one. */}
        <radialGradient id="lamp-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f5c518" stopOpacity="0.5" />
          <stop offset="45%" stopColor="#e5a93c" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#e5a93c" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ── the glow, behind everything ── */}
      <circle className="lamp-mark__glow" cx="60" cy="40" r="30" fill="url(#lamp-glow)" />

      {/* ── the lotus: five petals, opening from a single point ── */}
      <g
        className="lamp-mark__lotus"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* outer pair */}
        <path d="M60 96C60 96 40 94 30 84c-4-4-6-9-6-9s7-1 13 2c7 3 12 9 12 9" opacity="0.55" />
        <path d="M60 96C60 96 80 94 90 84c4-4 6-9 6-9s-7-1-13 2c-7 3-12 9-12 9" opacity="0.55" />
        {/* inner pair */}
        <path d="M60 96s-14-4-18-14c-2-5-2-11-2-11s6 2 10 7c5 6 8 15 8 15" opacity="0.8" />
        <path d="M60 96s14-4 18-14c2-5 2-11 2-11s-6 2-10 7c-5 6-8 15-8 15" opacity="0.8" />
        {/* the centre petal, which the lamp sits in */}
        <path d="M60 96c-6-4-9-11-9-18s4-13 9-17c5 4 9 10 9 17s-3 14-9 18Z" />
      </g>

      {/* ── the lamp ── */}
      <g
        className="lamp-mark__lamp"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* the bowl, resting in the centre petal */}
        <path d="M45 62c0 7 6.7 12 15 12s15-5 15-12" />
        <path d="M43.5 62h33" />
        {/* the butter, a line inside the rim rather than a drawn substance */}
        <path d="M50 66.5c3 1.6 6.4 2.4 10 2.4s7-.8 10-2.4" opacity="0.45" />
      </g>

      {/* ── the flame ── */}
      <g className="lamp-mark__flame">
        <path
          d="M60 33c5.2 4.6 8 9.2 8 13.8 0 4.9-3.6 8.7-8 8.7s-8-3.8-8-8.7c0-4.6 2.8-9.2 8-13.8Z"
          fill="#e5a93c"
        />
        <path
          d="M60 43.5c2.3 2.2 3.4 4.3 3.4 6.3 0 2.2-1.5 3.9-3.4 3.9s-3.4-1.7-3.4-3.9c0-2 1.1-4.1 3.4-6.3Z"
          fill="#fdfbf7"
          opacity="0.75"
        />
      </g>

      {/* ── one petal, adrift ── */}
      <path
        className="lamp-mark__petal"
        d="M60 78c-2.6-1.7-4-4.6-4-7.5s1.4-5.6 4-7.3c2.6 1.7 4 4.4 4 7.3s-1.4 5.8-4 7.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
